import React, { useState, useEffect } from "react";
import MyModal from "components/MyModal";
import * as yup from "yup";
import {
  indetityApiAppToken,
  useCreateEnterpriseUserMutation,
  useInviteUserMutation,
  useRequestChangePasswordMutation,
} from "redux/services/identity";
import {
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  RadioGroup,
  Radio,
  Stack,
} from "@chakra-ui/react";
import Alert from "components/Alert";
import { useDispatch } from "react-redux";
import {
  useGetOrganizationQuery,
  useGetProfileQuery,
  useGetUserMutation,
  useUpdateUserOrganisationMutation,
} from "redux/services/membership";
import { Roles } from "common";
import { getOrganisationMembership } from "utils";
import useCustomToast from "hooks/useCustomToast";
import { useGetUserRole } from "hooks/useGetUserRole";

interface CreateEnterpriseUserProps {
  readonly isOpen: boolean;
  readonly setOpenModalCreate: () => void;
}

const schema = yup.object().shape({
  firstName: yup.string().required("First Name is required"),
  lastName: yup.string().required("Last Name is required"),
  emailAddress: yup
    .string()
    .email("Invalid email")
    .required("Email is required"),
  // .max(8, 'The length must be between 0 and 8')
  bussinessUnitName: yup
    .string()
    .matches(/^[a-zA-Z\s]*$/, "Only alphabets and space are allowed")
    .required("Service Provider Name is required"),
  serviceProviderCode: yup
    .string()
    .min(2, "Service Provider Code must be at least 2 characters long")
    .max(
      15,
      "Service Provider Code must be less than or equal to 15 characters in length"
    )
    .matches(/^[A-Z]+$/, "Only uppercase alphabets without space are allowed")
    .required("Service Provider Code is required"),
});

function CreateEnterpriseUser(props: CreateEnterpriseUserProps) {
  const dispatch = useDispatch();
  const { showToast } = useCustomToast();
  const { isOpen, setOpenModalCreate } = props;
  const [
    getUser,
    {
      isSuccess: idGetUserSuccess,
      data: userData,
      isLoading: isLoadingGetUser,
    },
  ] = useGetUserMutation();

  const [
    updateOranization,
    {
      isError: isUpdateOranisationError,
      error: updateOranisationError,
      isSuccess: isUpdateOranisationSuccess,
      isLoading: isLoadingUpdateOrganization,
    },
  ] = useUpdateUserOrganisationMutation();

  const [
    createEnterpriseUserMutation,
    {
      isSuccess: createUserSuccess,
      error: createUserError,
      isLoading: isLoadingCreateEnterpriseUser,
      data: createDataResonse,
    },
  ] = useCreateEnterpriseUserMutation();

  const [
    inviteUserMutation,
    {
      isSuccess: inviteUserSuccess,
      error: inviteUserError,
      isLoading: isLoadingInviteUser,
    },
  ] = useInviteUserMutation();

  const [
    requestChangePasswordMutation,
    {
      isSuccess: requestPasswordChangeSuccess,
      isLoading: isLoadingRequestChangePassword,
    },
  ] = useRequestChangePasswordMutation();

  const { data: profileData } = useGetProfileQuery();
  const membershipOrganisation = getOrganisationMembership(
    profileData?.data?.memberships
  );
  const { isEnterpriseBankAdmin, isEnterpriseSalesTeamMember, isEnterpriseAdmin} = useGetUserRole();
  const [formData, setFormData] = useState<{
    firstName: string;
    lastName: string;
    emailAddress: string;
    bussinessUnitName: string;
    serviceProviderCode: string;
    userRole?: string;
  }>({
    firstName: "",
    lastName: "",
    emailAddress: "",
    bussinessUnitName: "",
    serviceProviderCode: "",
    userRole: isEnterpriseAdmin ? Roles.EnterpriseDeveloper: Roles.EnterpriseAdmin,
  });

  const [errors, setErrors] = useState<{
    firstName?: string;
    lastName?: string;
    emailAddress?: string;
    bussinessUnitName?: string;
    serviceProviderCode?: string;
    userRole?: string;
  }>({});

  const { data: organizationData } = useGetOrganizationQuery(
    {
      orgId: membershipOrganisation?.organisationId || "",
      userId: profileData?.data?.userId || "",
    },
    { skip: !(profileData && membershipOrganisation && !isEnterpriseBankAdmin && !isEnterpriseSalesTeamMember) }
  );

  useEffect(() => {
    return () => {
      dispatch(indetityApiAppToken.util.resetApiState());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (createUserSuccess) {
      requestProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createUserSuccess]);

  useEffect(() => {
    if (inviteUserSuccess) {
      showToast("success", "TPPP User successfully invited.");
      setOpenModalCreate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteUserSuccess]);

  useEffect(() => {
    if (isUpdateOranisationSuccess) {
      requestChangePasswordMuation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdateOranisationSuccess]);

  useEffect(() => {
    if (idGetUserSuccess && userData) {
      updateOranization({
        userId: userData.data.userId,
        orgId: userData.data.memberships[0].organisationId,
        orgRole: "TPPP", // Fixed
        orgType: "PCL", // Fixed
        companyName: formData.bussinessUnitName, // BU Name 100
        companyNumber: formData.serviceProviderCode, // Service Provide Code 100
        leiNumber: formData.serviceProviderCode, // BU Code
        status: "Active",
        externalIdentityList: [
          {
            externalSystem: "TYMEX",
            externalId: formData.serviceProviderCode,
          },
        ],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idGetUserSuccess, userData]);

  useEffect(() => {
    if (isOpen && organizationData && !isEnterpriseBankAdmin && !isEnterpriseSalesTeamMember) {
      setFormData({
        firstName: "",
        lastName: "",
        emailAddress: "",
        bussinessUnitName: organizationData?.data?.companyName,
        serviceProviderCode: organizationData?.data?.companyNumber,
        userRole: isEnterpriseAdmin ? Roles.EnterpriseDeveloper: Roles.EnterpriseAdmin,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, organizationData]);

  useEffect(() => {
    if (requestPasswordChangeSuccess) {
      showToast("success", "TPPP User successfully registered.");
      setOpenModalCreate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestPasswordChangeSuccess]);

  const requestProfile = () => {
    if (createDataResonse) getUser({ userId: createDataResonse?.data?.uuid });
  };

  const requestChangePasswordMuation = async () => {
    if (createDataResonse) {
      await requestChangePasswordMutation({
        userId: createDataResonse.data.uuid,
      });
    }
  };

  const handleChange = (e: any, useTrim?: boolean) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: useTrim ? value.trim() : value,
    });
  };

  const handleUserRoleChange = (value: string) => {
    setFormData({ ...formData, userRole: value });
  };

  const validateForm = async () => {
    try {
      await schema.validate(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (err: any) {
      const validationErrors: any = {};
      err.inner.forEach((error: any) => {
        validationErrors[error.path] = error.message;
      });
      setErrors(validationErrors);
      return false;
    }
  };

  const handleSubmit = async () => {
    const isValid = await validateForm();

    if (isValid) {
      if (isEnterpriseBankAdmin || isEnterpriseSalesTeamMember) {
        createEnterpriseUserMutation({
          ...formData,
        } as any);
      } else {
        inviteUserMutation([
          {
            emailAddress: formData.emailAddress,
            firstName: formData.firstName,
            lastName: formData.lastName,
            organisationId: organizationData?.data?.orgId,
            orgToken: membershipOrganisation?.token,
            memberships: [
              {
                roleName: "OrganisationUser",
                organisationId: organizationData?.data?.orgId || "",
              },
            ],
            roles: formData?.userRole ? [formData.userRole] : [],
          },
        ]);
      }
    }
  };

  return (
    <MyModal
      isOpen={isOpen}
      onClose={() => setOpenModalCreate()}
      title={isEnterpriseBankAdmin || isEnterpriseSalesTeamMember ? "Register TPPP User" : "Invite Collaborator"}
      submitBtnText={isEnterpriseBankAdmin || isEnterpriseSalesTeamMember ? "Register" : "Invite"}
      cancelBtnText="Cancel"
      onSubmit={() => handleSubmit()}
      loadingSubmit={
        isLoadingCreateEnterpriseUser ||
        isLoadingInviteUser ||
        isLoadingGetUser ||
        isLoadingUpdateOrganization ||
        isLoadingRequestChangePassword
      }
      scrollBehavior="inside"
      disabledCloseOutSide
    >
      {(createUserError || inviteUserError || isUpdateOranisationError) && (
        <Alert
          data-testid="alert-error"
          status="error"
          mb="10px"
          title={
            (
              (createUserError ||
                inviteUserError ||
                updateOranisationError) as any
            )?.data?.errors
              ?.map((err: any) => {
                if (err.code === "001.S01.409.07")
                  return "Service Provider Code already exists.";

                return err.message;
              })
              ?.join(", ") || "Something went wrong!"
          }
        />
      )}
      <FormControl mb="12px" isInvalid={!!errors.emailAddress}>
        <FormLabel>Email</FormLabel>
        <Input
          value={formData.emailAddress}
          name="emailAddress"
          data-testid="email-create-enterprise"
          isRequired
          onChange={handleChange}
        />
        {errors.emailAddress && (
          <FormErrorMessage fontSize={12}>
            {errors.emailAddress}
          </FormErrorMessage>
        )}
      </FormControl>
      <FormControl mb="12px" isInvalid={!!errors.firstName}>
        <FormLabel>First Name</FormLabel>
        <Input
          value={formData.firstName}
          name="firstName"
          data-testid="firstname-create-enterprise"
          isRequired
          onChange={handleChange}
        />
        {errors.firstName && (
          <FormErrorMessage fontSize={12}>{errors.firstName}</FormErrorMessage>
        )}
      </FormControl>
      <FormControl mb="12px" isInvalid={!!errors.lastName}>
        <FormLabel>Last Name</FormLabel>
        <Input
          value={formData.lastName}
          name="lastName"
          data-testid="lastname-create-enterprise"
          isRequired
          onChange={handleChange}
        />
        {errors.lastName && (
          <FormErrorMessage fontSize={12}>{errors.lastName}</FormErrorMessage>
        )}
      </FormControl>
      {isEnterpriseAdmin && (
        <FormControl mb="12px" data-testid="role-selector-create-enterprise">
          <FormLabel>Role</FormLabel>
          <RadioGroup value={formData.userRole} onChange={handleUserRoleChange}>
            <Stack direction="row" spacing={6}>
              <Radio value={Roles.EnterpriseDeveloper} data-testid="role-user-radio">
                User
              </Radio>
              <Radio value={Roles.EnterpriseAdmin} data-testid="role-admin-radio">
                Admin
              </Radio>
            </Stack>
          </RadioGroup>
        </FormControl>
      )}
      <FormControl mb="12px" isInvalid={!!errors.bussinessUnitName}>
        <FormLabel>Service Provider Name</FormLabel>
        <Input
          value={formData.bussinessUnitName}
          name="bussinessUnitName"
          data-testid="bussinessUnitName-create-enterprise"
          isRequired
          onChange={handleChange}
          disabled={!isEnterpriseBankAdmin && !isEnterpriseSalesTeamMember}
        />
        {errors.bussinessUnitName && (
          <FormErrorMessage fontSize={12}>
            {errors.bussinessUnitName}
          </FormErrorMessage>
        )}
      </FormControl>
      <FormControl mb="12px" isInvalid={!!errors.serviceProviderCode}>
        <FormLabel>Service Provider Code</FormLabel>
        <Input
          value={formData.serviceProviderCode}
          name="serviceProviderCode"
          data-testid="serviceProvideCode-create-enterprise"
          isRequired
          onChange={(e) => handleChange(e, true)}
          maxLength={15}
          disabled={!isEnterpriseBankAdmin && !isEnterpriseSalesTeamMember}
        />
        {errors.serviceProviderCode && (
          <FormErrorMessage fontSize={12}>
            {errors.serviceProviderCode}
          </FormErrorMessage>
        )}
      </FormControl>
    </MyModal>
  );
}

export default CreateEnterpriseUser;
